import { useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to manage navigation state and logic
 * 
 * @param {Array} navigationItems - Array of navigation item objects
 * @returns {Object} Navigation state and handlers
 */
const useNavigation = (navigationItems) => {
  const location = useLocation();
  
  // State for collapsible menu items
  const [openCollapsible, setOpenCollapsible] = useState({});

  // Toggle collapsible menu item
  const handleToggleCollapsible = useCallback((itemId) => {
    setOpenCollapsible(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  }, []);

  // Check if a navigation item is active based on current location
  const isItemActive = useCallback((path) => {
    if (!path || path === '#') return false;
    
    // Exact match for home route
    if (path === '/' && location.pathname === '/') return true;
    
    // For other routes, check if the current path starts with the item path
    // This handles nested routes (e.g., /users/123 should highlight the /users nav item)
    if (path !== '/') {
      return location.pathname.startsWith(path);
    }
    
    return false;
  }, [location.pathname]);

  // Find active navigation item
  const activeItem = useMemo(() => {
    // First check for direct matches
    const directMatch = navigationItems.find(item => isItemActive(item.path));
    if (directMatch) return directMatch;

    // Then check children of collapsible items
    for (const item of navigationItems) {
      if (item.children) {
        const childMatch = item.children.find(child => isItemActive(child.path));
        if (childMatch) {
          // Ensure the parent collapsible is open
          if (!openCollapsible[item.id]) {
            setOpenCollapsible(prev => ({
              ...prev,
              [item.id]: true
            }));
          }
          return childMatch;
        }
      }
    }
    
    return null;
  }, [navigationItems, isItemActive, openCollapsible]);

  return {
    openCollapsible,
    handleToggleCollapsible,
    isItemActive,
    activeItem
  };
};

export default useNavigation;

